import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useSiteBranding } from "@/context/SiteContext";
import { formatDate } from "@/lib/portalHelpers";

export const NewsEditorialCard = ({ article, basePath = "/insights", featured = false, index = 0 }) => {
  const { buildBrandedPath } = useSiteBranding();
  if (!article) return null;

  const destination = buildBrandedPath(`${basePath}/${article.slug || article.id}`);

  if (featured) {
    return (
      <Link
        className="group relative block min-h-[520px] overflow-hidden rounded-[34px] border border-[#001935]/10 bg-[#001935] shadow-[0_24px_80px_rgba(0,25,53,0.16)]"
        data-testid="news-featured-article-card"
        to={destination}
      >
        <img alt={article.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" src={article.image_url} />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,25,53,0.12)_0%,rgba(0,25,53,0.28)_34%,rgba(0,25,53,0.82)_100%)]" />
        <div className="relative flex h-full flex-col justify-end p-8 md:p-10">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#D4AF37]">{article.eyebrow || article.category}</p>
          <h2 className="mt-4 max-w-3xl text-4xl font-semibold leading-tight text-white md:text-5xl" data-testid="news-featured-article-title">
            {article.title}
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-7 text-white/82 md:text-base">{article.excerpt}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-white/78">
            <span data-testid="news-featured-article-date">{formatDate(article.published_at)}</span>
            <span>{article.read_time}</span>
            {article.key_stat ? <span className="rounded-full border border-white/15 px-4 py-1.5 text-xs uppercase tracking-[0.18em] text-[#F5D98C]">{article.key_stat}</span> : null}
          </div>
          <div className="mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#F5D98C]" data-testid="news-featured-article-link">
            Read the editorial <ArrowRight className="h-4 w-4" />
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      className="group block overflow-hidden rounded-[30px] border border-[#001935]/10 bg-white/90 shadow-[0_18px_60px_rgba(0,25,53,0.06)] transition duration-300 hover:-translate-y-1"
      data-testid={`news-editorial-card-${index + 1}`}
      to={destination}
    >
      <div className="overflow-hidden">
        <img alt={article.title} className="aspect-[1.22/1] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" src={article.image_url} />
      </div>
      <div className="space-y-4 p-6">
        <div className="flex flex-wrap items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-[#8b6d16]">
          <span>{article.eyebrow || article.category}</span>
          {article.market_focus ? <span className="rounded-full bg-[#F6EBC5] px-3 py-1 text-[11px] text-[#5c4921]">{article.market_focus}</span> : null}
        </div>
        <h3 className="text-3xl font-semibold leading-tight text-[#001935]" data-testid={`news-editorial-card-title-${index + 1}`}>{article.title}</h3>
        <p className="text-sm leading-7 text-[#42566f]">{article.excerpt}</p>
        <div className="flex items-center justify-between gap-4 text-sm text-[#6b6252]">
          <span>{formatDate(article.published_at)}</span>
          <span>{article.read_time}</span>
        </div>
        <div className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid={`news-editorial-card-link-${index + 1}`}>
          Continue reading <ArrowRight className="h-4 w-4" />
        </div>
      </div>
    </Link>
  );
};